/**
 * CustomCheckbox Component
 *
 * A reusable and stylized checkbox component that supports custom styling,
 * disabled state, and controlled checked state. Includes a built-in checkmark
 * SVG that appears when the checkbox is selected.
 *
 * Props:
 * @param {boolean} checked - Determines whether the checkbox is checked.
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} onChange - Callback function triggered when the checkbox state changes.
 * @param {boolean} disabled - If true, the checkbox is disabled and non-interactive.
 * @param {string} extraClassName - Optional additional className(s) for custom styling.
 * @param {string} id - Optional ID for the checkbox input.
 * @param {string} name - Optional name attribute for the checkbox input.
 *
 * Behavior:
 * - Checkbox supports controlled checked state.
 * - Disabled checkboxes are non-interactive.
 * - Displays a centered checkmark SVG when checked.
 * - Extra class names can be applied to customize styling.
 *
 * Usage:
 * ```tsx
 * <CustomCheckbox
 *   checked={isChecked}
 *   onChange={(e) => setIsChecked(e.target.checked)}
 *   disabled={false}
 *   extraClassName="my-custom-class"
 *   id="terms"
 *   name="terms"
 * />
 * ```
 *
 * Notes:
 * - The component is designed for inline use but can be wrapped or styled as needed.
 * - Uses TailwindCSS classes for styling, including peer and transition utilities.
 */

import React from "react";
import { CustomCheckboxProps } from "../../types/GeneralComponents";

const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  checked,
  onChange,
  disabled,
  extraClassName,
  id,
  name,
}) => {
  return (
    <div className="inline-flex items-center">
      <label className="relative flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={checked}
          className={`peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 transition-all checked:border-primaryMain checked:bg-primaryMain hover:shadow-md ${extraClassName}`}
          id={id}
          name={name}
          onChange={onChange}
          disabled={disabled}
        />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-white opacity-0 peer-checked:opacity-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5"
            viewBox="0 0 20 20"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            ></path>
          </svg>
        </span>
      </label>
    </div>
  );
};

export default CustomCheckbox;
